import React, { useState, useEffect } from 'react';
import md5 from 'md5';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Button, Card, Modal, Spinner, Alert } from 'react-bootstrap';
import styles from '../../../styles.module.css';
import useViewFiles from '../../../hooks/useViewFiles';
import { getPrefix } from '../../../util/utils';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import FileCarousel from '../../../components/laudos/FileCarousel';
import IcmHeaderImage from '../../../components/IcmHeaderImage';
import selectEcoById from '../../../services/ecodoppler';
import useIsMedicoExterno from '../../../hooks/useIsMedicoExterno';
import generateEcoCongenitoPDF from './generateEcoCongenitoPDF';

export default function ReportCong() {
  const { id } = useParams();
  const location = useLocation();
  const { printing = false } = location.state || {};
  const { data: eco, error, isLoading } = useSWR(id ? ['eco', id] : null, () =>
    selectEcoById(id)
  );
  const isPaciente = useSelector(state => Boolean(state.patient?.signed));
  const isMedicoExterno = useIsMedicoExterno() || isPaciente;
  const [isTimbradoPrint, setIsTimbradoPrint] = useState(printing);
  const [cpfPaciente, setcpfPaciente] = useState(false);

  useEffect(() => {
    if (isMedicoExterno) {
      setIsTimbradoPrint(true);
      return;
    }
    setIsTimbradoPrint(printing);
  }, [printing, isMedicoExterno]);

  const showTimbradoPrint = isMedicoExterno || isTimbradoPrint;
  const {
    urls,
    setURLs,
    loadingFiles,
    showModal,
    setShowModal,
    showFiles,
  } = useViewFiles({ codLaudo: eco?.CODECO, procedimento: 'eco' });
  const prefixoSolicitou = getPrefix(eco?.CODSOLICITANTE);
  let contador = 0;

  function removeDiv() {
    window.scrollTo(0, 0); // Scroll to the top of the page
    window.print();
  }

  const handleCPFPaciente = option => {
    setcpfPaciente(option === 'SIM');
  };

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de eco!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !eco) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <>
      <style>
        {`@media print {
          @page {
            margin-top:${showTimbradoPrint ? '0.8cm' : '4cm'};
            margin-bottom:4cm;
            margin-left: 2cm;
            margin-right: 2cm;
          }

          #removeMe {
            display: none !important;
          }
        }`}
      </style>
      <Card className="mt-2">
        {showTimbradoPrint && (
          <div className={styles.imageReport}>
            <IcmHeaderImage variant="compact" />
          </div>
        )}
        <LaudoCardHeader title="ECOCARDIOGRAMA CONGÊNITO" />
        {!isMedicoExterno && (
          <div className="text-center" id="removeMe">
            <label className="form-label font-weight-bold">
              Imprimir CPF do Paciente?
            </label>
            <div className=" align-items-center justify-content-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="cpfPaciente"
                  value="SIM"
                  checked={cpfPaciente === true}
                  onChange={() => handleCPFPaciente('SIM')}
                />
                <label className="form-check-label" htmlFor="cpfPacienteSim">
                  Sim
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="cpfPaciente"
                  value="NAO"
                  checked={cpfPaciente === false}
                  onChange={() => handleCPFPaciente('NAO')}
                />
                <label className="form-check-label" htmlFor="cpfPacienteNao">
                  Não
                </label>
              </div>
            </div>
          </div>
        )}
        <hr />
        <Card.Body>
          {eco && eco.TIPOLAUDO === 'Alterado' ? (
            <div>
              <strong className="mr-auto">Nome do Paciente: </strong>
              {`${eco.CODPAC} - ${eco.NOMEPAC}`}
              {cpfPaciente ? ` - CPF: ${eco?.CPFPAC}` : ''}
              <br></br>
              <strong className="mr-auto">Data de Nasc: </strong>
              {`${eco.dataNascFmt}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Sexo: </strong>
              {`${eco.Sexo}`}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Idade: </strong>
              {`${eco.idadePAC} anos`}
              <br></br>
              <strong className="mr-auto">Convênio: </strong>
              {eco.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Cod Laudo: </strong>
              {eco.CODECO}&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Data do Exame: </strong>
              {eco.dataEcoFmt}
              {/* <br></br>
              <strong className="mr-auto">
                Médico que Realizou: {prefixoRealizou}{' '}
              </strong>
              {`${eco.Realizou}`} */}
              <br></br>
              <strong className="mr-auto">
                Médico que Solicitou: {prefixoSolicitou}{' '}
              </strong>
              {`${eco.Solicitou}`}
              <hr></hr>
              {eco?.obs && (
                <>
                  {eco?.obs?.map(obs => (
                    <div className="row">
                      <div className="col-12"> {obs.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              )}
              <br></br>
              <strong className="mr-auto">
                Diâmetros e Função Sistólica do Ventrículo Esquerdo{' '}
              </strong>
              <div className="row">
                <div className="col-6">{`Septo: ${eco?.SEPTO ? eco.SEPTO.toFixed(1) + '(cm)' : 'nulo'
                  }`}</div>
                {eco.fracaoEjecaoMono !== null ? (
                  <div className="col-6">{`Fração de Ejeção(Monoplanar): ${eco.fracaoEjecaoMono}`}</div>
                ) : (
                  ''
                )}
              </div>
              <div className="row">
                <div className="col-6">
                  {`PPVE: ${eco.PPVE ? eco.PPVE.toFixed(1) + '(cm)' : 'nulo'}`}
                </div>
                {eco?.FRAEJECBIPLA !== null ? (
                  <div className="col-6">{`Fração de Ejeção(Simpson): ${eco.FRAEJECBIPLA.toFixed(
                    2
                  )}`}</div>
                ) : (
                  <div />
                )}
              </div>
              <div className="row">
                <div className="col-6">
                  {`Diâmetro Diastólico: ${eco.DD ? eco.DD.toFixed(1) + '(cm)' : 'nulo'
                    }`}
                </div>
                {eco?.massaVE !== null ? (
                  <div className="col-6">{`Massa do VE: ${Math.round(
                    eco?.massaVE
                  )} (g/m2)`}</div>
                ) : (
                  <div />
                )}
              </div>
              <div className="row">
                {eco?.DS !== null ? (
                  <div className="col-6">{`Diâmetro Sistólico: ${eco?.DS.toFixed(
                    1
                  )} (cm)`}</div>
                ) : (
                  <div />
                )}
              </div>
              {eco?.achados?.length > 0 ? (
                <>
                  <u className="mr-auto">Achados Descritivos do VE: </u>
                  {eco?.achados.map(achados => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {achados.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.contraRegiao?.length > 0 ? (
                <>
                  <u className="mr-auto">Contração Segmentar: </u>
                  {eco?.contraRegiao?.map(contra => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {contra.CONTRASEG}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">
                Átrio Esquerdo e Valva Mitral{' '}
              </strong>
              {eco?.DAE !== null ? (
                <div className="row">
                  <div className="col-12">
                    {' '}
                    {`Diâmetro do Átrio Esquerdo: ${eco?.DAE.toFixed(1)} (cm)`}
                  </div>
                </div>
              ) : null}
              {eco?.VOL_AE !== null ? (
                <div className="row">
                  <div className="col-12">
                    {`Volume do AE: ${eco?.VOL_AE.toFixed(2)} (cc/m2) (até 34)`}
                  </div>
                </div>
              ) : (
                ''
              )}
              {eco?.atrioEsq.length > 0 ? (
                <>
                  {eco?.atrioEsq?.map(atrioEsq => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {atrioEsq.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.valvaMitral.length > 0 ? (
                <>
                  {eco?.valvaMitral?.map(valvaMitral => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaMitral.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Valva Aórtica </strong>
              {eco?.DAVAO !== null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro da Raiz Aórtica: ${eco?.DAVAO.toFixed(
                    1
                  )} (cm)`}</div>
                </div>
              ) : (
                <div />
              )}
              {eco?.valvaAO.length > 0 ? (
                <>
                  {eco?.valvaAO?.map(valvaAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaAO.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Aorta </strong>
              {eco?.aorta.length > 0 ? (
                <>
                  {eco?.aorta?.map(aorta => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {aorta.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Átrio Direito </strong>
              {eco?.atrioDir.length > 0 ? (
                <>
                  {eco?.atrioDir?.map(atrioDir => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {atrioDir.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Veia Cava Inferior </strong>
              {eco?.veiaCavaInf.length > 0 ? (
                <>
                  {eco?.veiaCavaInf?.map(veiaCavaInf => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {veiaCavaInf.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Ventrículo Direito </strong>
              {eco?.ventriDir.length > 0 ? (
                <>
                  {eco?.ventriDir?.map(ventriDir => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {ventriDir.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <strong className="mr-auto">Valva Tricúspide</strong>
              {eco?.valvaTricu.length > 0 ? (
                <>
                  {eco?.valvaTricu?.map(valvaTricu => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaTricu.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              <div className="row">
                <div className="col-12">
                  <strong className="mr-auto">Valva Pulmonar</strong>
                </div>
              </div>
              {eco?.DAVP !== null ? (
                <>
                  <div className="row">
                    <div className="col-12">{`Diâmetro da Anel Pulmonar: ${eco?.DAVP.toFixed(
                      1
                    )} (cm)`}</div>
                  </div>
                  <br />
                </>
              ) : (
                <div />
              )}
              {eco?.valvaPulmo.length > 0 ? (
                <>
                  {eco?.valvaPulmo?.map(valvaPulmo => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaPulmo.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <div />
              )}
              {eco?.pericardio.length > 0 ? (
                <>
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Pericárdio</strong>
                    </div>
                  </div>
                  {eco?.pericardio?.map(pericardio => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {pericardio.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              <div className="row">
                <div className="col-12">
                  <p></p>
                  <strong className="mr-auto">Estudo Congênito</strong>
                </div>
              </div>
              {eco?.situs.length > 0 ? (
                <>
                  {eco?.situs?.map(situs => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {situs.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.septoIA.length > 0 ? (
                <>
                  {eco?.septoIA.map(septoIA => (
                    <div key={contador++} className="row">
                      <div className="col-12">
                        {' '}
                        {septoIA.DESCRICAO}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {septoIA.MEDIDA !== null
                          ? ` - Valor: ${septoIA.MEDIDA} cm`
                          : ''}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.septoIV.length > 0 ? (
                <>
                  {eco?.septoIV?.map(septoIV => (
                    <div key={contador++} className="row">
                      <div className="col-12">
                        {septoIV.septoIV}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {septoIV.MEDIDA !== null
                          ? ` - Valor: ${septoIV.MEDIDA} cm`
                          : ''}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.GVEVD !== null ? (
                <div className="row">
                  <div className="col-12">
                    {` Estimativa da PSAP: ${eco?.estimativaPSVDCong.toFixed(
                      1
                    )} (mm Hg)           Relação QP/QS: ${eco?.relacaoPSCong}`}
                  </div>
                </div>
              ) : (
                <div />
              )}
              {eco?.concordAV.length > 0 ? (
                <>
                  {eco?.concordAV?.map(concordAV => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {concordAV.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.ecoVS.length > 0 ? (
                <>
                  {eco?.ecoVS?.map(ecoVS => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {ecoVS.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.concordVA.length > 0 ? (
                <>
                  {eco?.concordVA?.map(concordVA => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {concordVA.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div />
              )}
              {eco?.artP.length > 0 ? (
                <>
                  {eco?.artP?.map(artP => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {artP.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.artC.length > 0 ? (
                <>
                  {eco?.artC?.map(artC => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {artC.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.arcoAO.length > 0 ? (
                <>
                  {eco?.arcoAO?.map(arcoAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {arcoAO.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.conexao.length > 0 ? (
                <>
                  {eco?.conexao?.map(conexao => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {conexao.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.estrutura.length > 0 ? (
                <>
                  {eco?.estrutura?.map(estrutura => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {estrutura.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {/* // FIM ESTUDO EcoCongenito */}
              {eco?.AVM !== null ||
                eco?.ORM !== null ||
                eco?.GMVM !== null ||
                eco?.VOLUME_VM !== null ||
                eco?.refVM.length > 0 ? (
                <div className="row">
                  <div className="col-12">
                    <p></p>
                    <strong className="mr-auto">Doppler Mitral</strong>
                  </div>
                </div>
              ) : null}
              <div className="row">
                {eco?.AVM !== null ? (
                  <div className="col-12">{`Área Valva Mitral: ${eco?.AVM.toFixed(
                    1
                  )} (cm2)   ${eco?.valorAVM}`}</div>
                ) : (
                  ''
                )}
                {eco?.GMVM !== null ? (
                  <div className="col-12">{`Gradiente Médio: ${eco?.GMVM} (mmHg)`}</div>
                ) : (
                  ''
                )}
                {eco?.relacaoIMAECLL ? (
                  <div className="col-12">
                    {`Relação ao Collor IM/AE: ${eco.relacaoIMAECLL}    ${eco?.valorRelacaoIMAECLL}`}
                  </div>
                ) : null}
                {eco?.ORIFICIO_VM != null ? (
                  <div className="col-12">{`Orifício Regurgitação Mitral (PISA): ${Number(
                    eco.ORIFICIO_VM
                  ).toFixed(2)} (cm2)`}</div>
                ) : null}
                {eco?.VOLUME_VM !== null ? (
                  <div className="col-12">{`Volume Regurgitação Mitral: ${eco?.VOLUME_VM} (cc)`}</div>
                ) : null}
                {eco?.DAM !== null ? (
                  <div className="col-12">{`Diâmetro do Anel Mitral: ${eco?.DAM} (cm)`}</div>
                ) : null}
                {!isNaN(eco?.FRM) ? (
                  <div className="col-12">{`Fração da Regurgitação Mitral: ${eco?.FRM} (%)        (Normal ou Trivial < 20%)`}</div>
                ) : null}
              </div>
              {eco?.refVM.length > 0 ? (
                <>
                  {eco?.refVM?.map(refVM => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVM.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.AREA_AO !== null ||
                eco?.GMVAO !== null ||
                eco?.GMVSVE !== null ||
                eco?.refVAO.length > 0 ? (
                <strong className="mr-auto">Doppler Aórtico</strong>
              ) : null}
              {eco?.AREA_AO !== null ? (
                <div className="row">
                  <div className="col-12">{`Área Valva Aórtica: ${eco?.AREA_AO.toFixed(
                    2
                  )} (cm2) ${eco?.valorAreaAVAO}`}</div>
                </div>
              ) : null}
              {!isNaN(eco?.FRA) ? (
                <div className="col-12">{`Fração da Regurgitação Aórtica: ${eco?.FRA} (%)        (Normal ou Trivial < 20%)`}</div>
              ) : null}
              {eco?.GMVAO !== null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Aórtica: ${eco?.GMVAO} (mmHg)`}</div>
                </div>
              ) : null}
              {eco?.GMVSVE !== null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio Via de Saída do VE: ${eco?.GMVSVE} (mmHg)`}</div>
                </div>
              ) : null}
              {eco?.relacaoIAOCLL ? (
                <div className="col-12">
                  {`Relação ao Collor da I Aórtica: ${eco.relacaoIAOCLL}    ${eco?.valorRelacaoIAOCLL}`}
                </div>
              ) : null}
              {eco?.refVAO.length > 0 ? (
                <>
                  {eco?.refVAO?.map(refVAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVAO.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : (
                <br></br>
              )}
              {eco?.GMVT !== null ||
                eco?.estimativaPSVD !== null ||
                eco?.refVT.length > 0 ? (
                <strong className="mr-auto">Doppler Tricúspide</strong>
              ) : null}
              {eco?.GMVT !== null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Tricúspide: ${eco?.GMVT} (mmHg)`}</div>
                </div>
              ) : null}
              {eco?.estimativaPSVD !== null ? (
                <div className="row">
                  <div className="col-12">{`Estimativa da Pressão Sistólica da AP: ${eco?.estimativaPSVD} (mmHg)`}</div>
                </div>
              ) : null}
              {eco?.refVT.length > 0 ? (
                <>
                  {eco?.refVT?.map(refVT => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVT.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GMVP !== null || eco?.refVP.length > 0 ? (
                <strong className="mr-auto">Doppler Pulmonar</strong>
              ) : null}
              {eco?.GMVP !== null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Pulmonar: ${eco?.GMVP} (mmHg)`}</div>
                </div>
              ) : null}
              {eco?.refVP.length > 0 ? (
                <>
                  {eco?.refVP?.map(refVP => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVP.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GMPM !== null || eco?.refluxoPM.length > 0 ? (
                <strong className="mr-auto">Prótese Mitral</strong>
              ) : null}
              {eco?.GMPM !== null ? (
                <>
                  <div className="row">
                    <div className="col-12">{`Gradiente Médio da Prótese Mitral: ${eco?.GMPM} (mmHg)`}</div>
                  </div>
                </>
              ) : null}
              {eco?.refluxoPM.length > 0 ? (
                <>
                  {eco?.refluxoPM?.map(refluxoPM => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refluxoPM.refluxoPM}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GMPAO !== null || eco?.refluxoPAO.length > 0 ? (
                <strong className="mr-auto">Prótese Aórtica</strong>
              ) : null}
              {eco?.GMPAO !== null ? (
                <>
                  <div className="row">
                    <div className="col-12">{`Gradiente Médio da Prótese Aórtica: ${eco?.GMPAO} (mmHg)`}</div>
                  </div>
                </>
              ) : null}
              {eco?.refluxoPAO.length > 0 ? (
                <>
                  {eco?.refluxoPAO?.map(refluxoPAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refluxoPAO.refluxoPAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GMPT !== null || eco?.refluxoPT.length > 0 ? (
                <strong className="mr-auto">Prótese Tricúspide</strong>
              ) : null}
              {eco?.GMPT !== null ? (
                <>
                  <div className="row">
                    <div className="col-12">{`Gradiente Médio da Prótese Tricúspide: ${eco?.GMPT} (mmHg)`}</div>
                  </div>
                </>
              ) : null}
              {eco?.refluxoPT.length > 0 ? (
                <>
                  {eco?.refluxoPT?.map(refluxoPT => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refluxoPT.refluxoPT}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GMPP !== null || eco?.refluxoPP.length > 0 ? (
                <strong className="mr-auto">Prótese Pulmonar</strong>
              ) : null}
              {eco?.GMPP !== null ? (
                <>
                  <div className="row">
                    <div className="col-12">{`Gradiente Médio da Prótese Pulmonar: ${eco?.GMPP} (mmHg)`}</div>
                  </div>
                </>
              ) : null}
              {eco?.refluxoPP.length > 0 ? (
                <>
                  {eco?.refluxoPP?.map(refluxoPP => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refluxoPP.refluxoPP}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.OBSERVACOES !== null ? (
                <>
                  <strong className="mr-auto">Observações</strong>
                  <div className="row">
                    <div className="col-12">{`${eco?.OBSERVACOES}`}</div>
                  </div>
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Conclusões</strong>
              {eco?.valvulopatia.length > 0 ? (
                <>
                  {eco?.valvulopatia?.map(valvu => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvu.valvulopatia}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.protese.length > 0 ? (
                <>
                  {eco?.protese?.map(prot => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {prot.protese}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.contraSeg.length > 0 ? (
                <>
                  {eco?.contraSeg?.map(contra => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {contra.contraSeg}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.conclusoes.length > 0 ? (
                <>
                  {eco?.conclusoes?.map(conclu => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {conclu.conclusoes}</div>
                    </div>
                  ))}
                </>
              ) : null}
              <Card className="mt-3 text-center">
                <Card.Body>
                  {eco?.CODCONV === 27 || eco?.CODCONV === 804
                    ? null
                    : urls.length > 0 && (
                      <div className={styles.messageReport}>
                        Imagens disponíveis no site:{' '}
                        <strong>www.icm.com.br (Resultado de Exames)</strong>
                        {/* TEMPORÁRIO */}
                        <br />
                        <span>Código de verificação: {eco?.CODPAC}</span>
                        <hr></hr>
                      </div>
                    )}
                  <div className={styles.signatureReport}>
                    <i className="mr-auto">
                      Conferido e assinado eletronicamente por:{' '}
                    </i>
                    <strong className="mr-auto">
                      {' '}
                      {getPrefix(eco?.CODREALIZOU)} {eco?.Realizou}
                    </strong>
                    <br></br>
                    <i className="mr-auto">
                      Assinatura Digital: {md5(JSON.stringify(eco))}
                    </i>
                  </div>
                </Card.Body>
                <Card.Footer id="removeMe" className="text-muted">
                  {!isMedicoExterno && (
                    <>
                      <Button
                        variant={
                          isTimbradoPrint ? 'outline-secondary' : 'secondary'
                        }
                        onClick={() => setIsTimbradoPrint(false)}
                        aria-label="Imprimir sem timbrado"
                      >
                        Sem Timbrado
                      </Button>{' '}
                      <Button
                        variant={
                          isTimbradoPrint ? 'secondary' : 'outline-secondary'
                        }
                        onClick={() => setIsTimbradoPrint(true)}
                        aria-label="Imprimir com timbrado"
                      >
                        Com Timbrado
                      </Button>{' '}
                      <Button
                        variant={
                          isTimbradoPrint ? 'outline-success' : 'success'
                        }
                        onClick={() => generateEcoCongenitoPDF(eco, 'imprimirTimbrado')}
                        aria-label="Imprimir com timbrado"
                      >
                        Visualizar PDF
                      </Button>{' '}
                    </>
                  )}
                  <Button onClick={() => removeDiv()}>Imprimir</Button>{' '}
                  <Button variant="success" onClick={() => showFiles()}>
                    Ver Imagem
                  </Button>
                </Card.Footer>
              </Card>
            </div>
          ) : (
            <div>
              <strong className="mr-auto">Nome do Paciente: </strong>
              {`${eco?.CODPAC} - ${eco?.NOMEPAC}`}
              {cpfPaciente ? ` - CPF: ${eco?.CPFPAC}` : ''}
              <br></br>
              <strong className="mr-auto">Data de Nasc: </strong>
              {`${eco?.dataNascFmt}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Sexo: </strong>
              {`${eco?.Sexo}`}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Idade: </strong>
              {`${eco?.idadePAC} anos`}
              <br></br>
              <strong className="mr-auto">Convênio: </strong>
              {eco?.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Cod Laudo: </strong>
              {eco?.CODECO}&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Data do Exame: </strong>
              {eco?.dataEcoFmt}
              {/*  <br></br>
              <strong className="mr-auto">
                Médico que Realizou: {prefixoRealizou}{' '}
              </strong>
              {`${eco?.Realizou}`} */}
              <br></br>
              <strong className="mr-auto">
                Médico que Solicitou: {prefixoSolicitou}{' '}
              </strong>
              {`${eco?.Solicitou}`}
              <hr></hr>
              {eco?.obs && (
                <>
                  {eco?.obs?.map(obs => (
                    <div className="row">
                      <div className="col-12"> {obs.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              )}
              <br></br>
              <strong className="mr-auto">
                Diâmetros e Função Sistólica do Ventrículo Esquerdo{' '}
              </strong>
              <div className="row">
                <div className="col-12">{`Septo: ${eco?.SEPTO &&
                  eco?.SEPTO.toFixed(1)} (cm)`}</div>
                {eco?.fracaoEjecaoMono !== null ? (
                  <div className="col-12">{`Fração de Ejeção(Monoplanar): ${eco.fracaoEjecaoMono}`}</div>
                ) : (
                  ''
                )}
              </div>
              <div className="row">
                <div className="col-12">{`PPVE: ${eco.PPVE &&
                  eco?.PPVE.toFixed(1)} (cm)`}</div>
              </div>
              <div className="row">
                <div className="col-12">{`Diâmetro Diastólico: ${eco?.DD &&
                  eco?.DD.toFixed(1)} (cm)`}</div>
              </div>
              <div className="row">
                <div className="col-12">{`Diâmetro Sistólico: ${eco?.DS &&
                  eco?.DS.toFixed(1)} (cm)`}</div>
              </div>
              {eco?.achados?.length > 0 ? (
                <>
                  <u className="mr-auto">Achados Descritivos do VE: </u>
                  {eco?.achados.map(achados => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {achados.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.contraRegiao && eco?.contraRegiao.length > 0 ? (
                <>
                  <u className="mr-auto">Contração Segmentar: </u>
                  {eco?.contraRegiao?.map(contra => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {contra.CONTRASEG}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">
                Átrio Esquerdo e Valva Mitral{' '}
              </strong>
              {eco?.DAE !== null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro do Átrio Esquerdo: ${eco?.DAE &&
                    eco?.DAE.toFixed(1)} (cm)`}</div>
                </div>
              ) : (
                ''
              )}
              {eco?.VOL_AE && eco?.VOL_AE !== null ? (
                <div className="row">
                  <div className="col-12">{`Volume do AE: ${eco?.VOL_AE} (cc/m2) (até 34)`}</div>
                </div>
              ) : (
                ''
              )}
              {eco?.atrioEsq && eco?.atrioEsq.length > 0 ? (
                <>
                  {eco?.atrioEsq?.map(atrioEsq => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {atrioEsq.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.valvaMitral && eco?.valvaMitral.length > 0 ? (
                <>
                  {eco?.valvaMitral?.map(valvaMitral => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaMitral.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Valva Aórtica </strong>
              {eco?.DAVAO !== null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro da Raiz Aórtica: ${eco?.DAVAO &&
                    eco?.DAVAO.toFixed(1)} (cm)`}</div>
                </div>
              ) : (
                ''
              )}
              {eco?.valvaAO && eco?.valvaAO.length > 0 ? (
                <>
                  {eco?.valvaAO?.map(valvaAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaAO.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Aorta </strong>
              {eco?.aorta && eco?.aorta.length > 0 ? (
                <>
                  {eco?.aorta?.map(aorta => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {aorta.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Átrio Direito </strong>
              {eco?.atrioDir && eco?.atrioDir.length > 0 ? (
                <>
                  {eco?.atrioDir?.map(atrioDir => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {atrioDir.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Ventrículo Direito </strong>
              {eco?.ventriDir && eco?.ventriDir.length > 0 ? (
                <>
                  {eco?.ventriDir?.map(ventriDir => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {ventriDir.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              <strong className="mr-auto">Valva Tricúspide</strong>
              {eco?.valvaTricu && eco?.valvaTricu.length > 0 ? (
                <>
                  {eco?.valvaTricu?.map(valvaTricu => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaTricu.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.valvaPulmo && eco?.valvaPulmo.length > 0 ? (
                <>
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Valva Pulmonar</strong>
                    </div>
                  </div>
                  {eco?.valvaPulmo?.map(valvaPulmo => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {valvaPulmo.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.pericardio && eco?.pericardio.length > 0 ? (
                <>
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Pericárdio</strong>
                    </div>
                  </div>
                  {eco?.pericardio?.map(pericardio => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {pericardio.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {/* INICIO ESTUDO CONGENITO */}
              <div></div>
              <div className="row">
                <div className="col-12">
                  <p></p>
                  <strong className="mr-auto">Estudo Congênito</strong>
                </div>
              </div>
              {eco?.situs.length > 0 ? (
                <>
                  {eco?.situs?.map(situs => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {situs.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.septoIA.length > 0 ? (
                <>
                  {eco?.septoIA?.map(septoIA => (
                    <>
                      <div key={contador++} className="row">
                        <div className="col-12">
                          {' '}
                          {septoIA.DESCRICAO}
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          {septoIA.MEDIDA !== null
                            ? ` - Valor: ${septoIA.MEDIDA} cm`
                            : ''}
                        </div>
                      </div>
                    </>
                  ))}
                </>
              ) : null}
              {eco?.septoIV.length > 0 ? (
                <>
                  {eco?.septoIV?.map(septoIV => (
                    <div key={contador++} className="row">
                      <div className="col-12">
                        {' '}
                        {septoIV.septoIV}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {septoIV.MEDIDA !== null
                          ? ` - Valor: ${septoIV.MEDIDA} cm`
                          : ''}
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.GVEVD !== null ? (
                <div className="row">
                  <div className="col-12">
                    {` Estimativa da PSAP: ${eco?.estimativaPSVDCong.toFixed(
                      1
                    )} (mm Hg)           Relação QP/QS: ${eco?.relacaoPSCong}`}
                  </div>
                </div>
              ) : (
                ''
              )}
              {eco?.concordAV.length > 0 ? (
                <>
                  {eco?.concordAV?.map(concordAV => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {concordAV.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.ecoVS.length > 0 ? (
                <>
                  {eco?.ecoVS?.map(ecoVS => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {ecoVS.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.concordVA.length > 0 ? (
                <>
                  {eco?.concordVA?.map(concordVA => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {concordVA.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.artP.length > 0 ? (
                <>
                  {eco?.artP?.map(artP => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {artP.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.artC.length > 0 ? (
                <>
                  {eco?.artC?.map(artC => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {artC.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.arcoAO.length > 0 ? (
                <>
                  {eco?.arcoAO?.map(arcoAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {arcoAO.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.conexao.length > 0 ? (
                <>
                  {eco?.conexao?.map(conexao => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {conexao.DESCRICAO}</div>
                    </div>
                  ))}
                </>
              ) : null}
              {eco?.refVM && eco?.refVM.length > 0 ? (
                <>
                  <div className="row">
                    <div className="col-12">
                      <p></p>
                      <strong className="mr-auto">Doppler Mitral</strong>
                    </div>
                  </div>
                  {eco?.refVM?.map(refVM => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVM.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.refVAO && eco?.refVAO.length > 0 ? (
                <>
                  <strong className="mr-auto">Doppler Aórtico</strong>
                  {eco?.refVAO?.map(refVAO => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVAO.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : (
                <br></br>
              )}
              {eco?.refVT && eco?.refVT.length > 0 ? (
                <>
                  <strong className="mr-auto">Doppler Tricúspide</strong>
                  {eco?.refVT?.map(refVT => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVT.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.refVP && eco?.refVP.length > 0 ? (
                <>
                  <strong className="mr-auto">Doppler Pulmonar</strong>
                  {eco?.refVP?.map(refVP => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {refVP.DESCRICAO}</div>
                    </div>
                  ))}
                  <br></br>
                </>
              ) : null}
              {eco?.OBSERVACOES && eco?.OBSERVACOES?.trim() !== '' ? (
                <>
                  <strong className="mr-auto">Observações</strong>
                  <div className="row">
                    <div className="col-12">{`${eco.OBSERVACOES}`}</div>
                  </div>
                  <br></br>
                </>
              ) : (
                ''
              )}
              <strong className="mr-auto">Conclusões</strong>
              {eco?.conclusoes && eco?.conclusoes.length > 0 ? (
                <>
                  {eco?.conclusoes?.map(conclu => (
                    <div key={contador++} className="row">
                      <div className="col-12"> {conclu.conclusoes}</div>
                    </div>
                  ))}
                </>
              ) : null}
              <Card className="mt-3 text-center">
                <Card.Body>
                  {eco?.CODCONV === 27 || eco?.CODCONV === 804
                    ? null
                    : urls.length > 0 && (
                      <div className={styles.messageReport}>
                        Imagens disponíveis no site:{' '}
                        <strong>www.icm.com.br (Resultado de Exames)</strong>
                        {/* TEMPORÁRIO */}
                        <br />
                        <span>Código de verificação: {eco?.CODPAC}</span>
                        <hr></hr>
                      </div>
                    )}
                  <div className={styles.signatureReport}>
                    <i className="mr-auto">
                      Conferido e assinado eletronicamente por:{' '}
                    </i>
                    <strong className="mr-auto">
                      {' '}
                      {getPrefix(eco?.CODREALIZOU)} {eco?.Realizou}
                    </strong>
                    <br></br>
                    <i className="mr-auto">
                      Assinatura Digital: {md5(JSON.stringify(eco))}
                    </i>
                  </div>
                </Card.Body>
                <Card.Footer id="removeMe" className="text-muted">
                  {!isMedicoExterno && (
                    <>
                      <Button
                        variant={
                          isTimbradoPrint ? 'outline-secondary' : 'secondary'
                        }
                        onClick={() => setIsTimbradoPrint(false)}
                        aria-label="Imprimir sem timbrado"
                      >
                        Sem Timbrado
                      </Button>{' '}
                      <Button
                        variant={
                          isTimbradoPrint ? 'secondary' : 'outline-secondary'
                        }
                        onClick={() => setIsTimbradoPrint(true)}
                        aria-label="Imprimir com timbrado"
                      >
                        Com Timbrado
                      </Button>{' '}
                      <Button
                        variant={
                          isTimbradoPrint ? 'outline-success' : 'success'
                        }
                        onClick={() => generateEcoCongenitoPDF(eco, 'imprimirTimbrado')}
                        aria-label="Imprimir com timbrado"
                      >
                        Visualizar PDF
                      </Button>{' '}
                    </>
                  )}
                  <Button onClick={() => removeDiv()}>Imprimir</Button>{' '}
                  <Button variant="success" onClick={showFiles}>
                    Ver Imagem
                  </Button>
                </Card.Footer>
              </Card>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
